<template>
  <div class="main-card">
    <!-- Skeleton Loading State -->
    <div
      v-if="isLoading"
      class="ps-prod-card ps-prod-card--skeleton"
      :class="{ 'ps-prod-card--dark': $vuetify.theme.dark }"
    >
      <div class="ps-prod-card__img ps-prod-card__img--skeleton"></div>
      <div class="ps-prod-card__skeleton-line ps-prod-card__skeleton-line--w60"></div>
      <div class="ps-prod-card__skeleton-line ps-prod-card__skeleton-line--w40"></div>
    </div>

    <!-- Actual Product Card (PosSmart v2 design) -->
    <div
      v-else
      class="ps-prod-card"
      :class="{
        'ps-prod-card--in-cart': haveInCart,
        'ps-prod-card--dark': $vuetify.theme.dark,
      }"
      role="button"
      tabindex="0"
      @click="handleProductClick(item)"
      @keydown.enter.prevent="handleProductClick(item)"
    >
      <!-- image area -->
      <div class="ps-prod-card__img">
        <v-img
          v-if="IMAGES(item.img)"
          class="ps-prod-card__photo"
          :src="IMAGES(item.img)"
          contain
        >
          <template v-slot:placeholder>
            <div class="ps-prod-card__placeholder">
              <v-icon size="30" color="rgba(91,57,201,0.35)"
                >fal fa-image</v-icon
              >
            </div>
          </template>
        </v-img>
        <v-icon v-else size="40" color="rgba(91,57,201,0.4)"
          >fal fa-shopping-bag</v-icon
        >

        <!-- stock / variant badge -->
        <span
          class="ps-prod-card__stock"
          :class="stockPillClass"
        >
          {{ stockPillText }}
        </span>

        <!-- qty badge (in cart) -->
        <span v-if="total" class="ps-prod-card__qty"
          >{{ total | ToNumber }}</span
        >

        <!-- remove button (in cart) -->
        <button
          v-if="haveInCart"
          class="ps-prod-card__remove"
          :aria-label="$t('remove') || 'Remove'"
          @click.stop="removeCart(item)"
        >
          <v-icon size="11">fal fa-trash-alt</v-icon>
        </button>
      </div>

      <!-- name -->
      <p class="ps-prod-card__name">{{ productName }}</p>

      <!-- category -->
      <p v-if="categoryName" class="ps-prod-card__category">
        {{ categoryName }}
      </p>

      <!-- price -->
      <p class="ps-prod-card__price">
        <v-chip
          v-if="isBuffetIncludedProduct(item)"
          x-small
          label
          outlined
          color="primary"
          class="rounded-lg"
        >
          {{ $t("free") }}
        </v-chip>
        <template v-else>
          {{ originPrice }}
          <span
            v-if="hasChannelPrice"
            class="ps-prod-card__channel-tag ml-1"
          >
            {{ activeChannelName }}
          </span>
        </template>
      </p>
    </div>
  </div>
</template>

<script>
import { myMixin } from "@/mixin/main";
import { mainStore } from "@/stores/modules/cart";

export default {
  props: {
    item: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },
  mixins: [myMixin],
  data() {
    return {
      defineStore: mainStore(),
      imageUrl: "/images/no_photo.jpg",
    };
  },

  computed: {
    total() {
      if (!this.item) return null;
      const value = this.defineStore.cart.find((e) => e.id === this.item.id);
      if (value != null && value != undefined) {
        return value.qty;
      }
    },
    haveInCart() {
      if (!this.item) return false;
      const value = this.defineStore.cart.find((e) => e.id === this.item.id);
      return value != null && value != undefined;
    },
    productName() {
      return this.item && this.item.name ? String(this.item.name) : "";
    },
    categoryName() {
      return this.item ? this.getCategoryName(this.item.category) : "";
    },
    // Reference-style stock pill: red when low, emerald otherwise
    stockPillClass() {
      if (this.item == null) return "ps-prod-card__stock--emerald";
      if (this.item.has_variations === 1) return "ps-prod-card__stock--soft";
      if (this.item.is_bundle === 1) return "ps-prod-card__stock--emerald";
      if (this.item.track_stock === 0) return "ps-prod-card__stock--slate";
      const stock = Number(this.item.stock) || 0;
      if (stock < 15) return "ps-prod-card__stock--red";
      return "ps-prod-card__stock--emerald";
    },
    stockPillText() {
      if (this.item == null) return "";
      if (this.item.has_variations === 1) return this.$t("variants_available");
      if (this.item.is_bundle === 1) return this.$t("bundle_stock") || "N/A";
      if (this.item.track_stock === 0) return this.$t("no_track_stock") || "N/A";
      const stock = Number(this.item.stock) || 0;
      return `${stock} ${this.$t("unit") || ""}`.trim();
    },
    hasChannelPrice() {
      if (!this.defineStore.selectedPriceChannelId || !this.item) return false;
      const list = this.item.channel_prices || this.item.channelPrices || [];
      return list.some(
        (cp) =>
          Number(cp.price_channel_id) ===
            Number(this.defineStore.selectedPriceChannelId) &&
          cp.is_active !== 0
      );
    },
    activeChannelName() {
      return this.defineStore.activePriceChannelName || "";
    },
    originPrice() {
      if (
        this.item &&
        this.item.price_sale &&
        this.item.price_sale > 0 &&
        this.item.currency &&
        this.item.currency.code &&
        this.item.currency.code !== null &&
        this.item.has_variations === 0
      ) {
        const effectivePrice = this.defineStore.getEffectiveProductPrice(
          this.item
        );
        return this.priceWithCurrency(effectivePrice, this.item.currency);
      } else if (
        this.item.has_variations === 1 &&
        this.item.product_options_count > 0
      ) {
        const _format = this.$options.filters.ToNumber(
          this.item.product_options_count
        );
        return `${_format} ${this.$t("variations")}`;
      } else if (this.item.dynamic_pricing === 1) {
        return this.$t("dynamic_pricing");
      } else if (this.item.is_bundle === 1) {
        return this.$t("bundle_stock");
      }
      return this.priceWithCurrency(0, null);
    },
  },
  methods: {
    async handleProductClick(item) {
      try {
        // Fetch latest product information from API as requested
        const { data } = await this.$http.get(`get_cart?q=${item.code}`);
        if (data.status && data.data) {
          const product = data.data;
          if (
            product.has_variations ||
            (product.product_variants && product.product_variants.length > 0) ||
            (product.product_options && product.product_options.length > 0)
          ) {
            this.$emit("show-variation-modal", product);
          } else if (product.dynamic_pricing) {
            this.$emit("show-dynamic-price-modal", product);
          } else if (product.size && product.size.unit === "dynamic") {
            this.$emit("show-pack-modal", product);
          } else {
            this.addToCart(product);
          }
        }
      } catch (error) {
        console.error("handleProductClick", error);
        // Fallback to local data if API fails
        if (item.has_variations && item.product_options_count > 0) {
          this.$emit("show-variation-modal", item);
        } else if (item.dynamic_pricing && !item.has_variations) {
          this.$emit("show-dynamic-price-modal", item);
        } else if (item.size && item.size.unit === "dynamic") {
          this.$emit("show-pack-modal", item);
        } else {
          this.addToCart(item);
        }
      }
    },

    async removeCart(item) {
      const body = {
        that: this,
        payload: item,
      };
      this.defineStore.removeCart(body);
    },

    async addToCart(item) {
      const body = {
        that: this,
        payload: item,
      };
      // Check if product is a pack and set unitAmount and packQty
      if (item.size && item.size.unit === "pack" && item.size.amount) {
        body.unitAmount = Number(item.size.amount) || 1;
        body.packQty = 1; // Default to 1 pack when adding directly
      }
      this.defineStore.addToCart(body);
    },
  },
};
</script>

<style scoped>
.main-card {
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  position: relative;
  height: 100%;
}
.main-card:hover {
  transform: translateY(-2px);
}

/* PosSmart v2 product card */
.ps-prod-card {
  background: #fff;
  border: 1px solid #e2e8f0; /* slate-200 */
  border-radius: 16px;
  padding: 8px;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.12s ease;
}
.ps-prod-card:hover {
  border-color: #5b39c9;
}
.ps-prod-card:active {
  transform: scale(0.97);
}
.ps-prod-card:focus-visible {
  outline: 2px solid #5b39c9;
  outline-offset: 2px;
}
.ps-prod-card--in-cart {
  border-color: #5b39c9;
}

.ps-prod-card__channel-tag {
  display: inline-block;
  font-size: 10px;
  line-height: 1.2;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 6px;
  background-color: #ede9fe;
  color: #5b39c9;
  vertical-align: middle;
}

/* image area */
.ps-prod-card__img {
  position: relative;
  height: 104px;
  border-radius: 12px;
  background: #f1f2f7;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.ps-prod-card__photo {
  width: 100%;
  height: 100%;
  padding: 6px;
}

.ps-prod-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f2f7;
}

/* stock pill */
.ps-prod-card__stock {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 9px;
  font-weight: 900;
  border-radius: 999px;
  padding: 2px 6px;
  letter-spacing: 0;
}
.ps-prod-card__stock--red {
  background: #fee2e2;
  color: #dc2626;
}
.ps-prod-card__stock--emerald {
  background: #d1fae5;
  color: #047857;
}
.ps-prod-card__stock--slate {
  background: #f1f5f9;
  color: #64748b;
}
.ps-prod-card__stock--soft {
  background: #f3f0fc;
  color: #5b39c9;
}

/* qty badge (in cart) — top-left, matches the design reference */
.ps-prod-card__qty {
  position: absolute;
  left: 7px;
  top: 7px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  box-sizing: border-box;
  border-radius: 999px;
  background: #5b39c9;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-variant-numeric: tabular-nums;
}

/* remove button (in cart) — bottom-right, out of the way of the qty badge */
.ps-prod-card__remove {
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fef2f2; /* light red (red-50) */
  color: #dc2626; /* red trash icon */
  border: 1px solid #cbd5e1; /* grey border */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;
}
.ps-prod-card__remove:hover {
  background: #fee2e2; /* red-100 */
  transform: scale(1.1);
}

/* name */
.ps-prod-card__name {
  margin: 10px 0 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  color: #141021;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 2.4em;
}

/* category */
.ps-prod-card__category {
  margin: 3px 0 0;
  font-size: 10.5px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* price */
.ps-prod-card__price {
  margin: 3px 0 0;
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: #141021;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* skeleton */
.ps-prod-card--skeleton {
  pointer-events: none;
}
.ps-prod-card__img--skeleton {
  background: linear-gradient(
    90deg,
    #f1f5f9 25%,
    #e2e8f0 50%,
    #f1f5f9 75%
  );
  background-size: 200px 100%;
  animation: ps-shimmer 1.5s infinite;
}
.ps-prod-card__skeleton-line {
  height: 14px;
  border-radius: 4px;
  margin-top: 10px;
  background: linear-gradient(
    90deg,
    #f1f5f9 25%,
    #e2e8f0 50%,
    #f1f5f9 75%
  );
  background-size: 200px 100%;
  animation: ps-shimmer 1.5s infinite;
}
.ps-prod-card__skeleton-line--w60 {
  width: 60%;
}
.ps-prod-card__skeleton-line--w40 {
  width: 40%;
}

@keyframes ps-shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

/* Dark mode support */
.ps-prod-card--dark,
.theme--dark .ps-prod-card {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.08);
}

.ps-prod-card--dark:hover,
.theme--dark .ps-prod-card:hover {
  border-color: #9a8cf0;
}

.ps-prod-card--dark.ps-prod-card--in-cart,
.theme--dark .ps-prod-card--in-cart {
  border-color: #9a8cf0;
  background: rgba(154, 140, 240, 0.1);
}

.ps-prod-card--dark .ps-prod-card__img,
.theme--dark .ps-prod-card__img {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.ps-prod-card--dark .ps-prod-card__placeholder,
.theme--dark .ps-prod-card__placeholder {
  background: #262638;
}

.ps-prod-card--dark .ps-prod-card__name,
.theme--dark .ps-prod-card__name {
  color: #ffffff;
}

.ps-prod-card--dark .ps-prod-card__category,
.theme--dark .ps-prod-card__category {
  color: #94a3b8;
}

.ps-prod-card--dark .ps-prod-card__price,
.theme--dark .ps-prod-card__price {
  color: #ffffff;
}

.ps-prod-card--dark .ps-prod-card__stock--slate,
.theme--dark .ps-prod-card__stock--slate {
  background: rgba(255, 255, 255, 0.1);
  color: #cbd5e1;
}

.ps-prod-card--dark .ps-prod-card__stock--soft,
.theme--dark .ps-prod-card__stock--soft {
  background: rgba(154, 140, 240, 0.2);
  color: #c4b5fd;
}

.ps-prod-card--dark .ps-prod-card__stock--emerald,
.theme--dark .ps-prod-card__stock--emerald {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

.ps-prod-card--dark .ps-prod-card__stock--red,
.theme--dark .ps-prod-card__stock--red {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.ps-prod-card--dark .ps-prod-card__remove,
.theme--dark .ps-prod-card__remove {
  background: rgba(239, 68, 68, 0.18);
  border-color: rgba(239, 68, 68, 0.35);
  color: #f87171;
}

.ps-prod-card--dark .ps-prod-card__img--skeleton,
.ps-prod-card--dark .ps-prod-card__skeleton-line,
.theme--dark .ps-prod-card__img--skeleton,
.theme--dark .ps-prod-card__skeleton-line {
  background: linear-gradient(
    90deg,
    #262638 25%,
    #31314a 50%,
    #262638 75%
  );
  background-size: 200px 100%;
}

@media (prefers-reduced-motion: reduce) {
  .main-card,
  .ps-prod-card {
    transition: none;
  }
}
</style>
